import { useTranslation } from 'react-i18next';
import type { TimelineEntry, TimelineEntryOrigin } from '../types/supportCase';

const ORIGIN_STYLES: Record<TimelineEntryOrigin, string> = {
  SYSTEM: 'bg-gray-100 text-gray-600',
  OWNER: 'bg-blue-50 text-blue-700',
  SUPPORT: 'bg-purple-50 text-purple-700',
  SCHEDULER: 'bg-yellow-50 text-yellow-700',
};

interface Props {
  readonly entries: ReadonlyArray<TimelineEntry>;
  readonly isLoading: boolean;
  readonly isError: boolean;
}

function dedupeEntries(entries: ReadonlyArray<TimelineEntry>): TimelineEntry[] {
  const seen = new Set<string>();
  const result: TimelineEntry[] = [];
  for (const entry of entries) {
    const key = `${entry.eventType}|${entry.occurredAt}|${entry.actorId ?? ''}`;
    if (!seen.has(key)) {
      seen.add(key);
      result.push(entry);
    }
  }
  return result;
}

export const BookingJourneyTimeline = ({ entries, isLoading, isError }: Props) => {
  const { t } = useTranslation('pages');

  if (isLoading) {
    return <p className="text-sm text-gray-500">{t('loadingCaseDetails')}</p>;
  }

  if (isError) {
    return <p className="text-sm text-red-600">{t('timelineLoadError')}</p>;
  }

  const sorted = dedupeEntries([...entries].sort(
    (a, b) => a.occurredAt.localeCompare(b.occurredAt),
  ));

  if (sorted.length === 0) {
    return <p className="text-sm text-gray-400 italic">{t('timelineEmpty')}</p>;
  }

  return (
    <ol className="relative border-l border-gray-200 space-y-4 pl-4">
      {sorted.map((entry, idx) => (
        <li key={`${entry.eventType}-${entry.occurredAt}-${idx}`} className="relative">
          <div className="absolute -left-[1.4rem] top-1 h-3 w-3 rounded-full border-2 border-white bg-gray-300" />
          <div className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium ${ORIGIN_STYLES[entry.origin]}`}>
            {entry.origin}
          </div>
          <p className="text-xs text-gray-500 mt-0.5">
            {new Date(entry.occurredAt).toLocaleString()}
          </p>
          <p className="text-sm text-gray-800 font-medium mt-0.5">{entry.eventType}</p>
          {entry.resultingBookingStatus && (
            <p className="text-xs text-gray-600">
              → {entry.resultingBookingStatus}
              {entry.resultingBookingVersion !== null && ` (v${entry.resultingBookingVersion})`}
            </p>
          )}
          {entry.outcome && (
            <p className="text-xs text-gray-500">Outcome: {entry.outcome}</p>
          )}
          {entry.actorId && (
            <p className="text-xs text-gray-400">Actor: {entry.actorId}</p>
          )}
        </li>
      ))}
    </ol>
  );
};
